/**
 * Bench calls for the customer portal: pairing, run list/detail, wipe
 * authorization and cosmetic grade. Thin wrappers over the Hono RPC client so
 * pages and tests share one call surface.
 */
import type {
  BenchCosmeticGrade,
  BenchRunDetail,
  BenchRunSummary,
  BenchStageDeselection,
} from '@siliconx/shared';
import { api, throwIfNotOk } from './api';

/** Same alphabet the API mints user codes from (no vowels, no digits). */
const USER_CODE_ALPHABET = /^[BCDFGHJKLMNPQRSTVWXZ]{8}$/;

/** Normalizes typed or scanned input to `XXXX-XXXX`, or null when it cannot be a user code. */
export function normalizeUserCodeInput(input: string): string | null {
  const compact = input.toUpperCase().replace(/[\s-]/g, '');
  if (!USER_CODE_ALPHABET.test(compact)) return null;
  return `${compact.slice(0, 4)}-${compact.slice(4)}`;
}

/** Mirrors GET /api/client/bench/pairings/:userCode (Task 10). Every string came from the device. */
export interface BenchPairingLookup {
  deviceSummary: {
    sysVendor: string | null;
    productName: string | null;
    chassisSerial: string | null;
    boardSerial: string | null;
    primaryMac: string | null;
    deviceCount: number | null;
  };
  sourceIp: string;
  siteMatch: boolean;
  expiresAt: string;
  /**
   * SIL-2000. Absent when test selection is off, for an attach pairing, when discovery could not be read or the profile
   * catalog is empty: the page then approves with every test, exactly as before. Validate with
   * benchPairingTestOptionsSchema before use (components/bench/testSelection.ts does).
   */
  testOptions?: unknown;
}

export async function lookupBenchPairing(code: string): Promise<BenchPairingLookup> {
  const res = await api.api.client.bench.pairings[':userCode'].$get({ param: { userCode: code } });
  await throwIfNotOk(res as unknown as Response);
  return (await res.json()) as unknown as BenchPairingLookup;
}

type ApproveRequest = { param: { userCode: string }; json?: { deselections: BenchStageDeselection[] } };

/**
 * `deselections` are the optional tests an org admin removed (SIL-2000). None or an empty list posts no body and no
 * content type, exactly as before. The approve route parses its body by hand (no zValidator), so the Hono client has no
 * typed json input: the request is cast, and the server is the validator.
 */
export async function approveBenchPairing(code: string, deselections?: BenchStageDeselection[]): Promise<{ runId: string }> {
  const post = api.api.client.bench.pairings[':userCode'].approve.$post as unknown as (args: ApproveRequest) => Promise<Response>;
  const res = await post(
    deselections && deselections.length > 0
      ? { param: { userCode: code }, json: { deselections } }
      : { param: { userCode: code } },
  );
  await throwIfNotOk(res);
  return (await res.json()) as unknown as { runId: string };
}

export async function denyBenchPairing(code: string): Promise<void> {
  const res = await api.api.client.bench.pairings[':userCode'].deny.$post({ param: { userCode: code } });
  await throwIfNotOk(res as unknown as Response);
}

export async function listBenchRuns(): Promise<BenchRunSummary[]> {
  const res = await api.api.client.bench.runs.$get();
  await throwIfNotOk(res as unknown as Response);
  return ((await res.json()) as unknown as { runs: BenchRunSummary[] }).runs;
}

export async function getBenchRun(runId: string): Promise<BenchRunDetail> {
  const res = await api.api.client.bench.runs[':id'].$get({ param: { id: runId } });
  await throwIfNotOk(res as unknown as Response);
  return (await res.json()) as unknown as BenchRunDetail;
}

/** `revoke` is the decline path: the bench skips the drive. The 6-digit code is required for `authorize`. */
export interface BenchWipeAuthorizationInput {
  deviceKeys: string[];
  action: 'authorize' | 'revoke';
  wipeCode?: string;
}

export async function authorizeBenchWipe(runId: string, body: BenchWipeAuthorizationInput): Promise<void> {
  const res = await api.api.client.bench.runs[':id']['wipe-authorizations'].$post({ param: { id: runId }, json: body });
  await throwIfNotOk(res as unknown as Response);
}

export async function setBenchCosmeticGrade(runId: string, grade: BenchCosmeticGrade): Promise<void> {
  const res = await api.api.client.bench.runs[':id']['cosmetic-grade'].$post({ param: { id: runId }, json: { grade } });
  await throwIfNotOk(res as unknown as Response);
}
